/**
 * Contract actions by status: specimen, generation (numbered, irreversible), sending the
 * signing link, signature at the office (WhatsApp code) or on paper, cancellation, amendment.
 */

import {
  type ContractDetail,
  useContractsItemsAmendCreate,
  useContractsItemsCancelCreate,
  useContractsItemsGenerateCreate,
  useContractsItemsPreviewCreate,
  useContractsItemsRefreshCreate,
  useContractsItemsRequestCodeCreate,
  useContractsItemsSendCreate,
  useContractsItemsSignCodeCreate,
  useContractsItemsSignPaperCreate,
} from "@agencyos/api-client";
import { Button, GlassCard, GlassSheet, TextAreaField, TextField } from "@agencyos/ui-web";
import { Copy, Eye, FileCheck2, FilePen, FileText, MessageCircle, RefreshCw, Send, Upload, XCircle } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useCurrentRole } from "../../lib/settings";
import { SpecimenLink } from "./SpecimenLink";
import { dateTime, errorText, useRefreshContracts } from "./shared";

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  return (
    <p role="alert" className="ko">
      {errorText(error, t("common.error"))}
    </p>
  );
}

function CopyLink({ url }: { url: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="contract-link">
      <code className="contract-link__url">{url}</code>
      <Button
        variant="secondary"
        icon={<Copy size={14} aria-hidden="true" />}
        onClick={() =>
          void navigator.clipboard?.writeText(url).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          })
        }
      >
        {copied ? t("contracts.actions.copied") : t("contracts.actions.copy")}
      </Button>
    </div>
  );
}

function GenerateButton({ contract, blocked }: { contract: ContractDetail; blocked: boolean }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const generate = useContractsItemsGenerateCreate({ mutation: { onSuccess: refresh } });
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <Button icon={<FileCheck2 size={16} aria-hidden="true" />} disabled={blocked || generate.isPending} onClick={() => setConfirming(true)}>
        {t("contracts.actions.generate")}
      </Button>
      <GlassSheet open={confirming} title={t("contracts.generate.title")} onClose={() => setConfirming(false)}>
        <div className="stack">
          <p>{t("contracts.generate.warning")}</p>
          <p className="muted">{t("contracts.generate.after")}</p>
          <ErrorLine error={generate.error} />
          <div className="row">
            <Button
              disabled={generate.isPending}
              onClick={() => generate.mutate({ id: contract.id }, { onSuccess: () => setConfirming(false) })}
            >
              {generate.isPending ? t("common.loading") : t("contracts.generate.confirm")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      </GlassSheet>
    </>
  );
}

/** Draft: proofread a specimen, follow the latest catalogue, then generate. */
function DraftActions({ contract, dirty }: { contract: ContractDetail; dirty: boolean }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const preview = useContractsItemsPreviewCreate();
  const sync = useContractsItemsRefreshCreate({ mutation: { onSuccess: refresh } });
  const specimen = preview.data?.status === 201 ? preview.data.data.id : null;
  const blocked = contract.blockers.length > 0 || dirty;
  return (
    <div className="stack">
      <div className="row">
        <Button variant="secondary" icon={<Eye size={16} aria-hidden="true" />} disabled={preview.isPending || dirty} onClick={() => preview.mutate({ id: contract.id })}>
          {t("contracts.actions.preview")}
        </Button>
        {contract.kind === "contract" ? (
          <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} disabled={sync.isPending} onClick={() => sync.mutate({ id: contract.id })}>
            {t("contracts.actions.refresh")}
          </Button>
        ) : null}
        <GenerateButton contract={contract} blocked={blocked} />
      </div>
      <p className="muted">
        {dirty ? t("contracts.actions.saveFirst") : contract.blockers.length ? t("contracts.actions.blocked") : t("contracts.actions.previewHelp")}
      </p>
      {specimen ? <SpecimenLink key={specimen} id={specimen} /> : null}
      <ErrorLine error={preview.error ?? sync.error} />
    </div>
  );
}

function SendBlock({ contract }: { contract: ContractDetail }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const send = useContractsItemsSendCreate({ mutation: { onSuccess: refresh } });
  const lastSent = [...contract.events].reverse().find((e) => e.kind === "sent");
  const channel = lastSent ? String((lastSent.data as Record<string, unknown> | null)?.channel ?? "") : "";
  const phone = (contract.parties?.client?.phone ?? "").replace(/\D/g, "");
  const shareText = t("contracts.send.shareText", { number: contract.number, url: contract.sign_url });
  return (
    <div className="stack">
      <h3>{t("contracts.send.title")}</h3>
      <p className="muted">{t("contracts.send.help")}</p>
      <div className="row">
        <Button icon={<Send size={16} aria-hidden="true" />} disabled={send.isPending} onClick={() => send.mutate({ id: contract.id })}>
          {contract.status === "sent" ? t("contracts.send.again") : t("contracts.send.action")}
        </Button>
      </div>
      {lastSent ? (
        <p className={channel === "link" ? "ko" : "ok"} role="status">
          {channel === "link"
            ? t("contracts.send.notDelivered")
            : t("contracts.send.sentVia", { channel: t(`contracts.channels.${channel || "link"}`), date: dateTime(lastSent.created_at, contract.language ?? "fr") })}
        </p>
      ) : null}
      {contract.sign_url ? (
        <>
          <CopyLink url={contract.sign_url} />
          <div className="row">
            <a
              className="ao-button ao-button--whatsapp"
              href={`https://wa.me/${phone}?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noreferrer"
            >
              <MessageCircle size={16} aria-hidden="true" /> {t("contracts.send.share")}
            </a>
          </div>
        </>
      ) : null}
      <ErrorLine error={send.error} />
    </div>
  );
}

/** At the office: the code goes to the client's WhatsApp, the client reads the 6 digits out. */
function OfficeSignature({ contract }: { contract: ContractDetail }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const request = useContractsItemsRequestCodeCreate();
  const sign = useContractsItemsSignCodeCreate({ mutation: { onSuccess: refresh } });
  const [code, setCode] = useState("");
  const channel = request.data?.status === 200 ? request.data.data.channel : "";
  if (contract.status !== "sent")
    return (
      <div className="stack">
        <h3>{t("contracts.office.title")}</h3>
        <p className="muted">{t("contracts.office.sendFirst")}</p>
      </div>
    );
  return (
    <div className="stack">
      <h3>{t("contracts.office.title")}</h3>
      <ol className="contract-steps muted">
        <li>{t("contracts.office.step1")}</li>
        <li>{t("contracts.office.step2")}</li>
        <li>{t("contracts.office.step3")}</li>
      </ol>
      <div className="row">
        <Button variant="whatsapp" disabled={request.isPending} onClick={() => request.mutate({ id: contract.id })}>
          {channel ? t("contracts.office.resend") : t("contracts.office.request")}
        </Button>
      </div>
      {channel ? (
        <p role="status" className="ok">
          {t("contracts.office.sent", { channel: t(`contracts.channels.${channel}`) })}
        </p>
      ) : null}
      <ErrorLine error={request.error} />
      {channel ? (
        <form
          className="row contract-code"
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length === 6) sign.mutate({ id: contract.id, data: { code } });
          }}
        >
          <TextField
            label={t("contracts.office.code")}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <Button type="submit" disabled={code.length !== 6 || sign.isPending}>
            {sign.isPending ? t("common.loading") : t("contracts.office.sign")}
          </Button>
        </form>
      ) : null}
      <ErrorLine error={sign.error} />
    </div>
  );
}

function PaperSignature({ contract }: { contract: ContractDetail }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const upload = useContractsItemsSignPaperCreate({ mutation: { onSuccess: refresh } });
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="stack">
      <h3>{t("contracts.paper.title")}</h3>
      <p className="muted">{t("contracts.paper.help")}</p>
      <input
        ref={input}
        type="file"
        className="visually-hidden"
        accept="application/pdf,image/*"
        aria-label={t("contracts.paper.upload")}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload.mutate({ id: contract.id, data: { scan: file } });
          e.target.value = "";
        }}
      />
      <div className="row">
        <Button variant="secondary" icon={<Upload size={16} aria-hidden="true" />} disabled={upload.isPending} onClick={() => input.current?.click()}>
          {upload.isPending ? t("common.loading") : t("contracts.paper.upload")}
        </Button>
      </div>
      <ErrorLine error={upload.error} />
    </div>
  );
}

function CancelSheet({ contract, open, onClose }: { contract: ContractDetail; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshContracts();
  const cancel = useContractsItemsCancelCreate({ mutation: { onSuccess: refresh } });
  const [reason, setReason] = useState("");
  return (
    <GlassSheet open={open} title={t("contracts.cancel.title")} onClose={onClose}>
      <div className="stack">
        <p className="muted">{t("contracts.cancel.help")}</p>
        <TextAreaField label={t("contracts.cancel.reason")} rows={3} maxLength={255} value={reason} onChange={(e) => setReason(e.target.value)} />
        <ErrorLine error={cancel.error} />
        <div className="row">
          <Button
            disabled={!reason.trim() || cancel.isPending}
            onClick={() => cancel.mutate({ id: contract.id, data: { reason: reason.trim() } }, { onSuccess: onClose })}
          >
            {t("contracts.cancel.confirm")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      </div>
    </GlassSheet>
  );
}

function AmendSheet({ contract, open, onClose }: { contract: ContractDetail; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const refresh = useRefreshContracts();
  const amend = useContractsItemsAmendCreate();
  const [fr, setFr] = useState("");
  const [en, setEn] = useState("");
  return (
    <GlassSheet open={open} title={t("contracts.amendment.new")} onClose={onClose}>
      <div className="stack">
        <p className="muted">{t("contracts.amendment.help")}</p>
        <TextAreaField label={t("contracts.amendment.text_fr")} rows={6} lang="fr" value={fr} onChange={(e) => setFr(e.target.value)} />
        <TextAreaField label={t("contracts.amendment.text_en")} rows={6} lang="en" value={en} onChange={(e) => setEn(e.target.value)} />
        <ErrorLine error={amend.error} />
        <div className="row">
          <Button
            disabled={!fr.trim() || amend.isPending}
            onClick={() =>
              amend.mutate(
                { id: contract.id, data: { text: { fr, en } } },
                {
                  onSuccess: (response) => {
                    if (response.status !== 201) return;
                    refresh();
                    onClose();
                    navigate(`/contracts/${response.data.id}`);
                  },
                },
              )
            }
          >
            {t("contracts.amendment.create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </GlassSheet>
  );
}

function DocumentLinks({ contract }: { contract: ContractDetail }) {
  const { t } = useTranslation();
  if (!contract.number) return null;
  return (
    <div className="row">
      {contract.document_url ? (
        <a className="ao-button ao-button--secondary" href={contract.document_url} target="_blank" rel="noreferrer">
          <FileText size={16} aria-hidden="true" /> {t("contracts.pdf.open")}
        </a>
      ) : (
        <span role="status" className="muted">
          {t("contracts.pdf.printing")}
        </span>
      )}
      {contract.scan_url ? (
        <a className="ao-button ao-button--ghost" href={contract.scan_url} target="_blank" rel="noreferrer">
          <FilePen size={16} aria-hidden="true" /> {t("contracts.pdf.scan")}
        </a>
      ) : null}
    </div>
  );
}

export function Lifecycle({ contract, dirty }: { contract: ContractDetail; dirty: boolean }) {
  const { t, i18n } = useTranslation();
  const role = useCurrentRole();
  const [cancelling, setCancelling] = useState(false);
  const [amending, setAmending] = useState(false);
  const canCancel = (role === "owner" || role === "manager") && ["draft", "ready", "sent"].includes(contract.status);
  const signing = contract.status === "ready" || contract.status === "sent";
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <h2>{t("contracts.actions.title")}</h2>
        {canCancel ? (
          <Button variant="ghost" icon={<XCircle size={16} aria-hidden="true" />} onClick={() => setCancelling(true)}>
            {t("contracts.cancel.open")}
          </Button>
        ) : null}
      </div>
      <DocumentLinks contract={contract} />
      {contract.status === "draft" ? <DraftActions contract={contract} dirty={dirty} /> : null}
      {signing ? (
        <>
          <SendBlock contract={contract} />
          <div className="grid-2 contract-sign-options">
            <OfficeSignature contract={contract} />
            <PaperSignature contract={contract} />
          </div>
        </>
      ) : null}
      {contract.status === "signed" ? (
        <div className="stack">
          <p className="ok">
            {t("contracts.signed.summary", {
              date: dateTime(contract.signed_at, i18n.language),
              method: contract.signature_method ? t(`contracts.method.${contract.signature_method}`) : "",
            })}
          </p>
          {contract.signed_hash ? (
            <p className="muted contract-hash">
              {t("contracts.signed.hash")} <code>{contract.signed_hash}</code>
            </p>
          ) : null}
          {contract.kind === "contract" ? (
            <div className="row">
              <Button variant="secondary" icon={<FilePen size={16} aria-hidden="true" />} onClick={() => setAmending(true)}>
                {t("contracts.amendment.new")}
              </Button>
              <span className="muted">{t("contracts.amendment.why")}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {contract.status === "cancelled" ? (
        <p role="note" className="ko">
          {t("contracts.cancel.done", { reason: contract.cancelled_reason })}
        </p>
      ) : null}
      <CancelSheet contract={contract} open={cancelling} onClose={() => setCancelling(false)} />
      <AmendSheet contract={contract} open={amending} onClose={() => setAmending(false)} />
    </GlassCard>
  );
}
